import React, { Component } from 'react';
import { removeSessionKey } from '../api/Common';
import { getActiveReplayId, noteReplayError } from './replay';
const DISABLED = window.location.hostname === 'localhost';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.apiClient = this.props.apiClient;

    // Bind error handlers
    // window.addEventListener('error', this.handleGlobalError);
    window.addEventListener(
      'unhandledrejection',
      this.handleUnhandledPromiseRejection,
    );
    window.onerror = this.handleGlobalError;

    // Override console methods
    // this.overrideConsoleMethods();
  }

  componentWillUnmount() {
    // Remove error handlers
    window.removeEventListener('error', this.handleGlobalError);
    window.removeEventListener(
      'unhandledrejection',
      this.handleUnhandledPromiseRejection,
    );
    window.onerror = null;

    // Restore original console methods
    this.restoreConsoleMethods();
  }

  overrideConsoleMethods() {
    const self = this;

    ['warn', 'error'].forEach(function (methodName) {
      const originalMethod = console[methodName];
      console[methodName] = function (...args) {
        // Call original method
        originalMethod.apply(console, args);

        // Log to error handler
        self.logToErrorHandler(methodName, args);
      };
    });
  }

  restoreConsoleMethods() {
    // Restore original console methods
    //console.log = this.originalConsoleLog;
    console.warn = this.originalConsoleWarn;
    console.error = this.originalConsoleError;
  }

  logToErrorHandler(method, args) {
    const message = args.join(' ');
    const error = new Error(`Console.${method}: ${message}`);
    this.logError(error, {
      componentStack: 'Console log',
    });
  }

  handleGlobalError = (event) => {
    const { message, source, lineno, colno, error } = event;
    console.error('Global error handler');
    this.logError(error, {
      componentStack: 'Global error handler',
      message: message,
      source: source,
      line: lineno,
      column: colno,
      errorStack: error ? error.stack : 'No stack trace',
    });
    // Return true to prevent the default handling of the error
    // return true;
  };

  handleUnhandledPromiseRejection = (event) => {
    const { reason } = event;
    console.error('Unhandled promise rejection:', reason);
    this.logError(reason, {
      componentStack: 'Unhandled Promise Rejection',
    });
    // You might log this error to your server, show a notification, etc.
  };

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by ErrorBoundary:', error);
    this.logError(error, errorInfo);
  }

  logError(error, errorInfo) {
    // A global "Script error." from a cross-origin/blob script (e.g. an
    // injected bundle) fires window.onerror with NO error object — event.error
    // is undefined. Guard so the logger itself never throws (that turned one
    // uncaught error into two, and ErrorBoundary.logError became the visible
    // crash). Fall back to the event message.
    const errorMessage =
      error == null
        ? errorInfo?.message || 'Unknown error (no error object)'
        : typeof error === 'string'
          ? error
          : error.message || error.toString();
    const componentStack = errorInfo?.componentStack || 'No stack trace';
    const errorString = `An error occurred: ${errorMessage}\nComponent stack: ${componentStack}`;
    // Log the error using the provided apiClient or perform any other action
    if (!DISABLED) {
      let oldError = sessionStorage.getItem('old_error');
      let currentDate = new Date().toISOString().split('T')[0]; // Get date without time
      let newError = `${currentDate} ${errorString}`;

      if (oldError === newError) {
        let counter = Number(sessionStorage.getItem('counter')) || 0;
        counter += 1;
        sessionStorage.setItem('counter', counter);

        if (counter > 50) {
          console.log('Logged out due to repeated errors.');
          removeSessionKey(this.apiClient);
          alert('Please re-login to Comprehend');
        }
      } else {
        sessionStorage.setItem('counter', 0);
      }

      // Count the error against the running replay even when the de-dupe below
      // suppresses the report: "this replay had 40 errors" is a ranking signal,
      // and only counting the first one would hide the worst sessions.
      noteReplayError();

      if (
        sessionStorage.getItem('error') !== errorString &&
        this.apiClient &&
        this.apiClient.logMessage
      ) {
        sessionStorage.setItem('error', errorString);
        sessionStorage.setItem('old_error', newError);
        // Stamping the replay id turns an error row into something watchable —
        // the whole point of the proactive-detection theme. Null when no replay
        // is running, which is the common case.
        this.apiClient.logMessage('error', errorString, getActiveReplayId());
      }
    }
  }

  render() {
    return this.props.children;
  }
}

export default ErrorBoundary;
